import { Link } from "react-router-dom";

export default function Features() {
  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 to-sky-700 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">
            AI assessments and secure testing in one workflow
          </h1>
          <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto">
            Create questions, assign secure tests, monitor student activity, and review timestamped logs from one teacher dashboard.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/teacher-register"
              className="px-8 py-4 bg-white hover:bg-gray-100 text-blue-600 rounded-lg font-semibold text-lg shadow-lg transition-all"
            >
              Create Your First Exam Free
            </Link>
            <Link
              to="/pricing"
              className="px-8 py-4 bg-blue-700 hover:bg-blue-800 text-white rounded-lg font-semibold text-lg border-2 border-white border-opacity-30 transition-all"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </div>

      {/* Core Features */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16 text-gray-900">Core Features</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">

            {/* AI Exam Builder */}
            <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100">
              <div className="text-4xl mb-4">🤖</div>
              <h3 className="text-2xl font-semibold mb-3 text-gray-900">AI Exam Builder</h3>
              <p className="text-gray-600 mb-4">
                Create questions from a topic, learning objective, or pasted lesson notes.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Multiple choice</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>True/False</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Fill in the blank</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Short answer</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Teacher review before assigning</li>
              </ul>
            </div>

            {/* Secure Test Mode */}
            <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100">
              <div className="text-4xl mb-4">🔒</div>
              <h3 className="text-2xl font-semibold mb-3 text-gray-900">Secure Test Mode</h3>
              <p className="text-gray-600 mb-4">
                Run assessments in a focused browser-based testing environment.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Fullscreen requirement</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Tab-switch detection</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Copy/paste blocking</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Serious test interface</li>
              </ul>
            </div>

            {/* Live Teacher Monitoring */}
            <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100">
              <div className="text-4xl mb-4">👁️</div>
              <h3 className="text-2xl font-semibold mb-3 text-gray-900">Live Teacher Monitoring</h3>
              <p className="text-gray-600 mb-4">
                See student progress, completion status, and integrity alerts during an assessment.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Progress tracking</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Completion status</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Alert count</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Live activity overview</li>
              </ul>
            </div>

            {/* Timestamped Activity Logs */}
            <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100">
              <div className="text-4xl mb-4">📋</div>
              <h3 className="text-2xl font-semibold mb-3 text-gray-900">Timestamped Activity Logs</h3>
              <p className="text-gray-600 mb-4">
                Review key assessment events after the test.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Fullscreen exits</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Tab switches</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Focus loss</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Copy/paste attempts</li>
              </ul>
            </div>

            {/* Results and Review */}
            <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-2xl font-semibold mb-3 text-gray-900">Results and Review</h3>
              <p className="text-gray-600 mb-4">
                Review submissions, scores, and question performance.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Auto-marked question types</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Manual review for written responses</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Student submissions</li>
              </ul>
            </div>

            {/* Browser-Based Access */}
            <div className="bg-white p-8 rounded-xl shadow-md border border-gray-100">
              <div className="text-4xl mb-4">🌐</div>
              <h3 className="text-2xl font-semibold mb-3 text-gray-900">Browser-Based Access</h3>
              <p className="text-gray-600 mb-4">
                Students open the assessment in their browser. No app installation required.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Works on laptops and Chromebooks</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>No app install</li>
                <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Teacher-controlled sessions</li>
              </ul>
            </div>

          </div>
        </div>
      </div>

      {/* Comparison Section */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-6 text-gray-900">
            Built for secure assessment, not just quizzes
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            See how ExamLock compares to standard quiz tools
          </p>

          <div className="max-w-4xl mx-auto bg-gray-50 border border-gray-200 rounded-xl p-8">
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Standard Quiz Tools</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start"><span className="text-gray-400 mr-2">•</span>Good for practice and review</li>
                  <li className="flex items-start"><span className="text-gray-400 mr-2">•</span>Limited secure monitoring</li>
                  <li className="flex items-start"><span className="text-gray-400 mr-2">•</span>Often focused on engagement</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-blue-700 mb-4">ExamLock</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>AI assessment creation</li>
                  <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Secure test environment</li>
                  <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Teacher monitoring</li>
                  <li className="flex items-start"><span className="text-blue-600 mr-2">✓</span>Timestamped activity logs</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Coming Soon */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4 text-gray-900">Coming Soon</h2>
          <p className="text-center text-gray-600 mb-12">
            Features currently in development
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-lg border-2 border-dashed border-gray-300 text-center">
              <div className="text-3xl mb-2">📄</div>
              <div className="font-medium text-gray-700">Upload PDF</div>
            </div>
            <div className="bg-white p-6 rounded-lg border-2 border-dashed border-gray-300 text-center">
              <div className="text-3xl mb-2">🔗</div>
              <div className="font-medium text-gray-700">Website Import</div>
            </div>
            <div className="bg-white p-6 rounded-lg border-2 border-dashed border-gray-300 text-center">
              <div className="text-3xl mb-2">🎓</div>
              <div className="font-medium text-gray-700">Google Classroom</div>
            </div>
            <div className="bg-white p-6 rounded-lg border-2 border-dashed border-gray-300 text-center">
              <div className="text-3xl mb-2">📚</div>
              <div className="font-medium text-gray-700">Practice Mode</div>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Ready to transform your assessment workflow?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Join teachers in private beta testing ExamLock.
          </p>
          <Link
            to="/teacher-register"
            className="inline-block px-8 py-4 bg-white hover:bg-gray-100 text-blue-600 rounded-lg font-semibold text-lg shadow-lg transition-all"
          >
            Create Your First Exam Free
          </Link>
        </div>
      </div>

    </div>
  );
}
